import * as React from "react";
import { observer, inject } from "mobx-react";
import { Link, withRouter, RouteComponentProps, RouteProps } from "react-router-dom";
import { UncontrolledTooltip } from "reactstrap";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faPenSquare, faTrashAlt } from "@fortawesome/free-solid-svg-icons";

import LoadingWidget from "../common/LoadingWidget";
import { SimpleConfirmation } from "../common/SimpleConfirmAlert";
import NewHorseDialog, { NewHorseDialog as NewHorseDialogType } from "./NewHorseDialog";
import { successMessage, errorMessage } from "../common/Toast";

import { HorseListStore } from "../../stores/HorseListStore";
import { ConfigurationStore } from "../../stores/ConfigurationStore";

import { SimpleHorse, HorseSex, HorseGait } from "../../model/Horse";
import { HorseLink } from "../common/HorseLink";

export interface Props extends RouteComponentProps<any> {
    horseListStore?: HorseListStore;
    configurationStore?: ConfigurationStore;
}
export interface State {
    isLoading: boolean;
    newHorseDialogVisible: boolean;
}
@inject("configurationStore")
@inject("horseListStore")
@observer
export class HorseList extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            isLoading: false,
            newHorseDialogVisible: false
        };
    }

    handleSearchOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        this.props.horseListStore!.updateHorsesListFilters({ fullTextSearch: e.target.value });
    };

    tableChanged = (type: TableChangeType, event: TableChangeNewState) => {
        if (type === "pagination") {
            this.props.horseListStore!.updateHorseListState({
                page: event.page,
                sizePerPage: event.sizePerPage
            });
        } else if (type === "sort") {
            this.props.horseListStore!.updateHorseListState({
                sortOrder: event.sortOrder,
                sortField: event.sortField as any
            });
        }
    };

    deleteHorse = (horse: SimpleHorse) => async (event: React.MouseEvent<any>) => {
        event.preventDefault();
        event.stopPropagation();

        const goAhead = await SimpleConfirmation(`Are you sure you'd like to delete ${horse.name}?`);
        if (!goAhead) {
            return;
        }

        try {
            await this.props.horseListStore!.deleteHorse(horse.id);
            successMessage("The selected horse has been deleted.");
        } catch (error) {
            errorMessage(error);
        }
    };

    handleDialogHorse = (e: React.MouseEvent<any>) => {
        e.preventDefault();
        this.setState({ newHorseDialogVisible: true });
    };

    componentDidMount() {
        this.props.horseListStore!.resetAndRefresh();
    }

    render() {
        const { horseListStore } = this.props;
        if (!horseListStore) {
            return <div>No Horses found.</div>;
        }
        const { horseListFilters, horseListState } = horseListStore;

        const columns = [
            {
                dataField: "name",
                text: "Name",
                sort: true,
                order: "asc"
            },
            {
                dataField: "sire",
                text: "Sire",
                formatter: (cellContent: SimpleHorse, row: SimpleHorse) => row.sire && <HorseLink horse={row.sire} targetBlank={true} />
            },
            {
                dataField: "dam",
                text: "Dam",
                formatter: (cellContent: SimpleHorse, row: SimpleHorse) => row.dam && <HorseLink horse={row.dam} targetBlank={true} />
            },
            {
                dataField: "sex",
                text: "Sex",
                formatter: (cellContent: HorseSex, row: SimpleHorse) => {
                    return cellContent && cellContent.name;
                }
            },
            {
                dataField: "gait",
                text: "Gait",
                formatter: (cellContent: HorseGait, row: SimpleHorse) => {
                    return cellContent && cellContent.name;
                }
            },
            {
                dataField: "id",
                text: "Actions",
                headerStyle: { width: "100px" },
                formatter: (cellContent: any, row: SimpleHorse) => {
                    return (
                        <div className="btn-group btn-group-toggle">
                            {/* <Link 
                                to={`horse/${row.id}/`} 
                                className="btn btn-dark btn-sm" 
                                id={`tooltipOpen${row.id}`} 
                                target="_blank"
                                title="Open Horse"
                            >
                                <FontAwesomeIcon icon={faExternalLinkSquareAlt}/>
                            </Link>
                            <UncontrolledTooltip placement="top" target={`tooltipOpen${row.id}`} delay={0} className="tooltipstrap">
                                Open Horse
                            </UncontrolledTooltip> */}
                            <Link to={`horse/${row.id}/`} className="btn btn-secondary btn-sm" id={`tooltipEdit${row.id}`} title="Edit Horse">
                                <FontAwesomeIcon icon={faPenSquare} />
                            </Link>
                            <UncontrolledTooltip placement="top" target={`tooltipEdit${row.id}`} delay={0} className="tooltipstrap">
                                Edit Horse
                            </UncontrolledTooltip>
                            {/* Only provide the delete option if the customer is unreferenced */}
                            <button
                                onClick={this.deleteHorse(row)}
                                id={`tooltipDelete${row.id}`}
                                className="btn btn-secondary btn-sm"
                                disabled={row.referenced}
                            >
                                <FontAwesomeIcon icon={faTrashAlt} />
                            </button>
                            <UncontrolledTooltip placement="top" target={`tooltipDelete${row.id}`} delay={0} className="tooltipstrap">
                                Delete Horse
                            </UncontrolledTooltip>
                        </div>
                    );
                }
            }
        ];

        return (
            <div>
                <nav aria-label="breadcrumb">
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item active" aria-current="page">
                            Horses
                        </li>
                    </ol>
                </nav>
                <div className="content">
                    <div className="row">
                        <div className="col-8">
                            <h1>Horse List</h1>
                        </div>
                        <div className="col-4">
                            <button
                                type="button"
                                className="btn btn-light float-right shadow"
                                data-toggle="button"
                                aria-pressed="false"
                                onClick={this.handleDialogHorse}
                            >
                                Create New Horse
                            </button>
                        </div>
                    </div>

                    <div className="card shadow-sm mr-4">
                        <div className="card-body">
                            <form className="">
                                <div className="form-row">
                                    <div className="col-12">
                                        <div className="form-row">
                                            <div className="col-12">
                                                <div className="form-group">
                                                    <label htmlFor="firstname">Search</label>
                                                    <div className="input-group">
                                                        <div className="input-group-prepend">
                                                            <div className="input-group-text">
                                                                <FontAwesomeIcon icon={faSearch} />
                                                            </div>
                                                        </div>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            id="firstname"
                                                            placeholder="Search..."
                                                            name="firstName"
                                                            value={horseListFilters.fullTextSearch}
                                                            onChange={this.handleSearchOnChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>

                    <div className="row mt-4">
                        <div className="col-12">
                            <div className="table-responsive">
                                {
                                    <BootstrapTable
                                        keyField="id"
                                        remote={true}
                                        hover={true}
                                        bordered={false}
                                        columns={columns}
                                        bootstrap4={true}
                                        data={horseListStore.horseList.results}
                                        pagination={paginationFactory({
                                            page: horseListState.page,
                                            sizePerPage: horseListState.sizePerPage,
                                            totalSize: horseListStore.horseList.total
                                        })}
                                        onTableChange={this.tableChanged}
                                        noDataIndication={() => (
                                            <div>{horseListStore.loading ? <LoadingWidget /> : <span>No results found.</span>}</div>
                                        )}
                                    />
                                }
                                {/* This is here as a hack to make the component observe `customerListStore.loading` 
                                    Otherwise the `noDataIndication` wouldn't work */}
                                {horseListStore.loading && <div />}
                            </div>
                        </div>
                    </div>
                </div>
                <NewHorseDialog
                    horseListStore={this.props.horseListStore!}
                    configurationStore={this.props.configurationStore!}
                    show={this.state.newHorseDialogVisible}
                    onClose={() => this.setState({ newHorseDialogVisible: false })}
                />
            </div>
        );
    }
}

export default withRouter<Props>(HorseList);



// WEBPACK FOOTER //
// ./src/components/horses/HorseList.tsx